import { useCallback, useMemo, useState } from "react";
import type { RenterDriverError } from "@fm/api";
import { canAddAnotherDriver } from "../../lib/renter-drivers";
import {
  CONTACT_KEYS,
  fromServerErrors,
  isDraftReady,
  newDraft,
  toRequestDriver,
  validateDraft,
  type ContactKey,
  type DriverDraft,
  type DriverFieldErrors,
  type DriverFormKey,
} from "./draft";

export interface RenterDriverCardsState {
  drivers: DriverDraft[];
  bookerDrives: boolean;
  errors: Record<string, DriverFieldErrors>;
  /** The renter pressed Continue (or a server check failed) while not ready. */
  tried: boolean;
  readyCount: number;
  allReady: boolean;
  canAdd: boolean;
  /**
   * The contact fields this card must ask for. An insurer needs email and
   * phone, except the booker's: the checkout has theirs, unless the server
   * refused one, which then stays on the card.
   */
  contactKeys: (index: number) => readonly ContactKey[];
  setBookerDrives: (value: boolean) => void;
  setField: (key: string, field: DriverFormKey, value: string) => void;
  save: (key: string) => void;
  edit: (key: string) => void;
  remove: (key: string) => void;
  add: () => void;
  markTried: () => void;
  /** Maps a FLEE-442 422 onto the cards; reopens the ones that failed. */
  applyServerError: (error: RenterDriverError) => void;
  buildRequest: () => {
    booker_drives: boolean;
    renter_drivers: ReturnType<typeof toRequestDriver>[];
  };
}

/**
 * The drivers typed at the booking-site checkout. State lives here only: the
 * request body is the one place the data leaves.
 */
export function useRenterDriverCards(
  returnDate: string,
  config: { max_additional_drivers?: number | null } | undefined,
  contactRequired = false,
): RenterDriverCardsState {
  const [drivers, setDrivers] = useState<DriverDraft[]>(() => [newDraft()]);
  const [bookerDrives, setBookerDrives] = useState(true);
  const [errors, setErrors] = useState<Record<string, DriverFieldErrors>>({});
  const [tried, setTried] = useState(false);

  const [refused, setRefused] = useState<Record<string, ContactKey[]>>({});

  const contactKeys = useCallback(
    (index: number): readonly ContactKey[] => {
      if (contactRequired && !(index === 0 && bookerDrives)) {
        return CONTACT_KEYS;
      }

      const key = drivers[index]?.key;
      return (key && refused[key]) || [];
    },
    [contactRequired, bookerDrives, drivers, refused],
  );

  const clearError = (key: string, field?: DriverFormKey) =>
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      if (field) {
        const { [field]: _dropped, ...rest } = next[key]!;
        next[key] = rest;
      } else {
        delete next[key];
      }
      return next;
    });

  const setField = useCallback(
    (key: string, field: DriverFormKey, value: string) => {
      setDrivers((prev) =>
        prev.map((driver) =>
          driver.key === key
            ? { ...driver, fields: { ...driver.fields, [field]: value } }
            : driver,
        ),
      );
      clearError(key, field);
    },
    [],
  );

  const save = useCallback(
    (key: string) => {
      const index = drivers.findIndex((candidate) => candidate.key === key);
      const driver = drivers[index];
      if (!driver) return;

      const found = validateDraft(
        driver.fields,
        returnDate,
        undefined,
        contactKeys(index),
      );
      if (Object.keys(found).length > 0) {
        setErrors((prev) => ({ ...prev, [key]: found }));
        return;
      }

      clearError(key);
      setDrivers((prev) =>
        prev.map((candidate) =>
          candidate.key === key ? { ...candidate, saved: true } : candidate,
        ),
      );
    },
    [drivers, returnDate, contactKeys],
  );

  const edit = useCallback((key: string) => {
    setDrivers((prev) =>
      prev.map((driver) =>
        driver.key === key ? { ...driver, saved: false } : driver,
      ),
    );
  }, []);

  const remove = useCallback((key: string) => {
    setDrivers((prev) =>
      prev.length > 1 ? prev.filter((driver) => driver.key !== key) : prev,
    );
    clearError(key);
  }, []);

  const canAdd = canAddAnotherDriver(drivers.length, config);

  const add = useCallback(() => {
    setDrivers((prev) =>
      canAddAnotherDriver(prev.length, config) ? [...prev, newDraft()] : prev,
    );
  }, [config]);

  const applyServerError = useCallback(
    (error: RenterDriverError) => {
      const next: Record<string, DriverFieldErrors> = {};
      drivers.forEach((driver, index) => {
        const mapped = fromServerErrors(error.driverErrors[index]);
        if (Object.keys(mapped).length > 0) next[driver.key] = mapped;
      });

      setTried(true);
      setErrors(next);
      setRefused((prev) => {
        const kept = { ...prev };
        for (const [key, found] of Object.entries(next)) {
          const keys = CONTACT_KEYS.filter(
            (field) => found[field] || prev[key]?.includes(field),
          );
          if (keys.length > 0) kept[key] = keys;
        }
        return kept;
      });
      setDrivers((prev) =>
        prev.map((driver) =>
          next[driver.key] ? { ...driver, saved: false } : driver,
        ),
      );
    },
    [drivers],
  );

  const readyCount = useMemo(
    () =>
      drivers.filter((driver, index) =>
        isDraftReady(driver, returnDate, contactKeys(index)),
      ).length,
    [drivers, returnDate, contactKeys],
  );

  const buildRequest = useCallback(
    () => ({
      booker_drives: bookerDrives,
      renter_drivers: drivers.map(toRequestDriver),
    }),
    [bookerDrives, drivers],
  );

  return {
    drivers,
    bookerDrives,
    errors,
    tried,
    readyCount,
    allReady: drivers.length > 0 && readyCount === drivers.length,
    canAdd,
    contactKeys,
    setBookerDrives,
    setField,
    save,
    edit,
    remove,
    add,
    markTried: () => setTried(true),
    applyServerError,
    buildRequest,
  };
}
